import React, { useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import './FileDiffViewer.css';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';

type Props = {
    oldText: string;
    newText: string;
};

const normalizeText = (text: string): string => {
    return text.replace(/\r\n/g, '\n');
};

const decodeNewlines = (text: string): string => {
    return text.replace(/\\n/g, '\n');
};

const FileDiffViewer: React.FC<Props> = ({ oldText, newText }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const normalizedOld = decodeNewlines(normalizeText(oldText));
    const normalizedNew = decodeNewlines(normalizeText(newText));
    const [copiedOriginal, setCopiedOriginal] = useState(false);
    const [copiedModified, setCopiedModified] = useState(false);

    const handleCopyOriginal = () => {
        navigator.clipboard.writeText(normalizedOld);
        setCopiedOriginal(true);
        setTimeout(() => setCopiedOriginal(false), 2000);
    };

    const handleCopyModified = () => {
        navigator.clipboard.writeText(normalizedNew);
        setCopiedModified(true);
        setTimeout(() => setCopiedModified(false), 2000);
    };

    const OriginalHeader = () => (
        <Box 
            className="diff-header"
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                bgcolor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                py: 1,
                px: 2
            }}
        >
            <Typography 
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#E2E8F0' : '#1F2937'
                }}
            >
                Original Code
            </Typography>
            <Tooltip title={copiedOriginal ? "Copied!" : "Copy code"}>
                <IconButton 
                    size="small" 
                    onClick={handleCopyOriginal}
                    sx={{ 
                        padding: '4px',
                        color: isDarkMode ? '#CBD5E1' : '#6B7280',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                            color: isDarkMode ? '#F1F5F9' : '#111827'
                        }
                    }}
                >
                    {copiedOriginal ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );

    const ModifiedHeader = () => (
        <Box 
            className="diff-header"
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                bgcolor: isDarkMode ? '#1A1A1A' : '#FFFFFF',
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                py: 1,
                px: 2
            }}
        >
            <Typography 
                variant="subtitle2"
                sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#E2E8F0' : '#1F2937'
                }}
            >
                Modified Code
            </Typography>
            <Tooltip title={copiedModified ? "Copied!" : "Copy code"}>
                <IconButton 
                    size="small" 
                    onClick={handleCopyModified}
                    sx={{ 
                        padding: '4px',
                        color: isDarkMode ? '#CBD5E1' : '#6B7280',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                            color: isDarkMode ? '#F1F5F9' : '#111827'
                        }
                    }}
                >
                    {copiedModified ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <div className={`diff-vertical-scroll-container ${isDarkMode ? 'dark-theme' : ''}`}>
            <ReactDiffViewer
                oldValue={normalizedOld}
                newValue={normalizedNew}
                splitView={true}
                showDiffOnly={false}
                leftTitle={<OriginalHeader />}
                rightTitle={<ModifiedHeader />}
                useDarkTheme={isDarkMode}
                styles={{
                    contentText: {
                        whiteSpace: 'pre' as const,
                        overflow: 'visible' as const,
                    },
                    diffContainer: {
                        display: 'flex',
                        minWidth: 'auto',
                    },
                    line: {
                        wordBreak: 'normal' as const,
                        whiteSpace: 'pre' as const,
                    },
                    gutter: {
                        width: '3rem',
                    },
                    codeFoldGutter: {
                        width: '2rem',
                    },
                    titleBlock: {
                        position: 'sticky' as const,
                        top: 0,
                        zIndex: 10,
                    }
                }}
            />
        </div>
    );
};

export default FileDiffViewer;